/**
 * Reliable overflow detection for CSS -webkit-line-clamp.
 *
 * Comparing offsetHeight vs scrollHeight on a clamped element is flaky for
 * Quill HTML (nested <p>/<ul>/<li>), where scrollHeight often equals the
 * clamped height. Measure by cloning without the clamp class instead.
 */
export function isLineClampOverflowing(
  element: HTMLElement | null | undefined,
  limitedClass = 'limited'
): boolean {
  if (!element || !element.isConnected) {
    return false;
  }

  const clampedHeight = element.offsetHeight;
  const width = element.offsetWidth;
  if (clampedHeight === 0 || width === 0) {
    return false;
  }

  // Already expanded — treat as overflowing so the toggle can still hide.
  if (!element.classList.contains(limitedClass)) {
    return true;
  }

  const clone = element.cloneNode(true) as HTMLElement;
  clone.classList.remove(limitedClass);
  clone.removeAttribute('id');
  clone.style.cssText = [
    'position: absolute',
    'visibility: hidden',
    'pointer-events: none',
    'height: auto',
    'max-height: none',
    'overflow: visible',
    'display: block',
    '-webkit-line-clamp: unset',
    'line-clamp: unset',
    `-webkit-box-orient: initial`,
    `width: ${width}px`,
    'left: -99999px',
    'top: 0',
    'z-index: -1',
  ].join(';');

  const parent = element.parentElement || document.body;
  parent.appendChild(clone);
  const fullHeight = clone.offsetHeight;
  parent.removeChild(clone);

  // 1px slack for subpixel rounding
  return fullHeight > clampedHeight + 1;
}
